import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaGraduationCap, FaPlay } from "react-icons/fa";
import { toast } from "sonner";
import {
  getUserCourses,
  getCourses,
} from "../../../store/slices/courses/asyncThunks";
import {
  selectUserCourses,
  selectUserCoursesLoading,
  selectUserCoursesError,
  selectCourses,
} from "../../../store/slices/courses/slice";
import { loadCourseLessons } from "../../../store/slices/lessonProgress/asyncThunks";
import CourseCard from "../../../components/CourseCard/CourseCard";
import {
  PageHeader,
  PageTitle,
  CoursesGrid,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  LoadingContainer,
  Button,
  ContinueHero,
  ContinueThumb,
  ContinueInfo,
  ContinueLabel,
  ContinueTitle,
  ContinueProgressRow,
  ContinueProgressBar,
  ContinueProgressFill,
  ContinueButton,
  OtherCoursesTitle,
} from "./MyCoursesPage.styles";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../../api";

const MyCoursesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const userCourses = useSelector(selectUserCourses);
  const loading = useSelector(selectUserCoursesLoading);
  const error = useSelector(selectUserCoursesError);
  const allCourses = useSelector(selectCourses);

  const [continueLoading, setContinueLoading] = useState(false);

  // Fetch user courses on component mount
  useEffect(() => {
    dispatch(getUserCourses());
  }, [dispatch]);

  // Fetch all courses for the "Ostale edukacije" section
  useEffect(() => {
    if (!allCourses || allCourses.length === 0) {
      dispatch(getCourses());
    }
  }, [dispatch, allCourses]);

  // "Nastavi gde si stala" — poslednji korišćen kurs koji nije završen ni istekao
  const continueCourse = (() => {
    const candidates = userCourses.filter((uc) => {
      const c = uc.course;
      if (!c) return false;
      const expired =
        Boolean(uc.expiryDate) && new Date(uc.expiryDate) < new Date();
      return c.hasStarted && (c.progress ?? 0) < 100 && !expired;
    });

    if (candidates.length === 0) return null;

    return candidates.reduce((latest, current) => {
      const latestTime = latest.lastAccessedAt
        ? new Date(latest.lastAccessedAt).getTime()
        : 0;
      const currentTime = current.lastAccessedAt
        ? new Date(current.lastAccessedAt).getTime()
        : 0;
      return currentTime > latestTime ? current : latest;
    });
  })();

  // Vodi direktno u lekciju na kojoj je korisnica stala
  const handleContinue = async () => {
    if (!continueCourse || continueLoading) return;
    const courseId = continueCourse.course.id;

    setContinueLoading(true);
    try {
      const result = await dispatch(loadCourseLessons(courseId)).unwrap();
      const lessons = result.lessons || [];

      if (lessons.length === 0) {
        toast.info("Kurs je u razvoju - lekcije će uskoro biti dostupne");
        return;
      }

      // Ista strategija kao na kartici: lekcija sa najviše progresa,
      // pa prva otključana, pa poslednja
      let targetLesson = null;

      const lessonsWithProgress = lessons.filter(
        (lesson) =>
          lesson.isUnlocked &&
          !lesson.progress?.isCompleted &&
          (lesson.progress?.watchedDuration || 0) > 0,
      );

      if (lessonsWithProgress.length > 0) {
        targetLesson = lessonsWithProgress.reduce((latest, current) => {
          const latestDuration = latest.progress?.watchedDuration || 0;
          const currentDuration = current.progress?.watchedDuration || 0;
          return currentDuration > latestDuration ? current : latest;
        });
      }

      if (!targetLesson) {
        targetLesson = lessons.find((lesson) => lesson.isUnlocked);
      }

      if (!targetLesson) {
        targetLesson = lessons[lessons.length - 1];
      }

      navigate(
        `/korisnicki-panel/kursevi/${courseId}/lekcije/${targetLesson.id}`,
      );
    } catch (err) {
      toast.error("Greška prilikom učitavanja lekcija");
    } finally {
      setContinueLoading(false);
    }
  };

  // "Ostale edukacije" — aktivni kursevi koje korisnica još nema
  const ownedCourseIds = new Set(
    userCourses.map((uc) => uc.courseId || uc.course?.id).filter(Boolean),
  );
  const otherCourses = (allCourses || []).filter(
    (c) => !ownedCourseIds.has(c.id) && c.isActive !== false,
  );

  if (loading) {
    return (
      <LoadingContainer>
        <l-line-spinner size="24" stroke="2" speed="1" color="var(--accent)" />
        <p>Učitavam vaše kurseve...</p>
      </LoadingContainer>
    );
  }

  if (error && userCourses.length === 0) {
    return (
      <EmptyState>
        <EmptyIcon>
          <FaGraduationCap />
        </EmptyIcon>
        <EmptyTitle>Greška pri učitavanju</EmptyTitle>
        <EmptyDescription>
          Došlo je do greške prilikom učitavanja vaših kurseva. Molimo pokušajte
          ponovo.
        </EmptyDescription>
        <Button $primary onClick={() => dispatch(getUserCourses())}>
          Pokušaj ponovo
        </Button>
      </EmptyState>
    );
  }

  return (
    <>
      <PageHeader>
        <PageTitle>Moji Kursevi</PageTitle>
      </PageHeader>

      {/* Nastavi gde si stala */}
      {continueCourse && (
        <ContinueHero>
          <ContinueThumb
            src={`${BASE_URL}/uploads/courses-thumbnails/${continueCourse.course.thumbnailImage}`}
            alt={`${continueCourse.course.title} thumbnail`}
          />
          <ContinueInfo>
            <ContinueLabel>Nastavi gde si stala</ContinueLabel>
            <ContinueTitle>{continueCourse.course.title}</ContinueTitle>
            <ContinueProgressRow>
              <ContinueProgressBar>
                <ContinueProgressFill
                  style={{
                    width: `${Math.min(
                      Math.max(continueCourse.course.progress ?? 0, 0),
                      100,
                    )}%`,
                  }}
                />
              </ContinueProgressBar>
              <span>{continueCourse.course.progress ?? 0}% završeno</span>
            </ContinueProgressRow>
          </ContinueInfo>
          <ContinueButton onClick={handleContinue} disabled={continueLoading}>
            <FaPlay />
            {continueLoading ? "Učitavam..." : "Nastavi lekciju"}
          </ContinueButton>
        </ContinueHero>
      )}

      {userCourses.length > 0 ? (
        <CoursesGrid>
          {userCourses.map((userCourse) => {
            return (
              <CourseCard
                key={userCourse.courseId || userCourse.id}
                course={{
                  ...userCourse.course,
                  progress: userCourse.course?.progress ?? 0,
                  hasStarted: userCourse.course?.hasStarted || false,
                  lastAccessedAt: userCourse.lastAccessedAt,
                  expiryDate: userCourse.expiryDate,
                }}
                variant="mycourses"
                animate={true}
              />
            );
          })}
        </CoursesGrid>
      ) : (
        <EmptyState>
          <EmptyIcon>
            <FaGraduationCap />
          </EmptyIcon>
          <EmptyTitle>Nema kurseva</EmptyTitle>
          <EmptyDescription>
            Još uvek nemate kupljenih kurseva. Istražite naše dostupne kurseve
            da biste započeli učenje.
          </EmptyDescription>
          <Button
            $primary
            onClick={() => {
              navigate("/kursevi");
              setTimeout(() => {
                window.scrollTo(0, 0);
              }, 300);
            }}
          >
            Kupi kurs
          </Button>
        </EmptyState>
      )}

      {/* Cross-sell: edukacije koje korisnica još nema */}
      {userCourses.length > 0 && otherCourses.length > 0 && (
        <>
          <OtherCoursesTitle>Ostale Edukacije</OtherCoursesTitle>
          <CoursesGrid>
            {otherCourses.map((course) => (
              <CourseCard key={course.id} course={course} animate={true} />
            ))}
          </CoursesGrid>
        </>
      )}
    </>
  );
};

export default MyCoursesPage;
